import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { API_URL } from '../apiUrl';
import { SocialContext } from './socialContext';

const ChatStateContext = React.createContext();


const ChatStateProvider = React.memo(({ children }) => {
  const { me } = useContext(SocialContext);
  const isLoggedIn = Boolean(me?.user?.token);

  const [currentChat, setCurrentChat] = useState(null);
  const [unread, setUnread] = useState([]);
  const [unauthorized, setUnauthorized] = useState(false);
  const isPollingRef = useRef(false);

  const fetchUnreadCount = useCallback(() => {
    if (!isLoggedIn || unauthorized || isPollingRef.current) return;
    isPollingRef.current = true;

    fetch(`${API_URL}/api/message/count`)
      .then(response => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.json();
      })
      .then(data => {
        if (data && !data.status) {
          setUnauthorized(false);
          setUnread(prev =>
            JSON.stringify(prev) !== JSON.stringify(data) ? data : prev
          );
        } else if (data && data.status === 404) {
          // keep unauthorized
        } else {
          setUnauthorized(false);
          setUnread(prev =>
            JSON.stringify(prev) !== JSON.stringify(data) ? data : prev
          );
        }
      })
      .catch(() => {
        setUnauthorized(true);
      })
      .finally(() => {
        isPollingRef.current = false;
      });
  }, [isLoggedIn, unauthorized]);

  useEffect(() => {
    if (!isLoggedIn) {
      setUnauthorized(true);
      setUnread([]);
      return;
    }
    setUnauthorized(false);
    fetchUnreadCount();
  }, [isLoggedIn, fetchUnreadCount]);

  useEffect(() => {
    if (!isLoggedIn || unauthorized) return;
    const id = setInterval(fetchUnreadCount, 10000);
    const onVisChange = () => {
      if (!document.hidden) fetchUnreadCount();
    };
    document.addEventListener('visibilitychange', onVisChange);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisChange);
    };
  }, [isLoggedIn, unauthorized, fetchUnreadCount]);

  const sendQuickMessage = useCallback(async (to, text) => {
    try {
      await fetch(`${API_URL}/api/message/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user: to, text }),
      });
    } catch (e) {
      console.error('sendQuickMessage error:', e);
    }
  }, []);

  const value = useMemo(() => ({
    currentChat,
    setCurrentChat,
    unread,
    unauthorized,
    sendQuickMessage,
    refreshUnread: fetchUnreadCount,
  }), [currentChat, unread, unauthorized, sendQuickMessage, fetchUnreadCount]);

  return (
    <ChatStateContext.Provider value={value}>
      {children}
    </ChatStateContext.Provider>
  );
});

export { ChatStateContext, ChatStateProvider };
